// extended dayjs for utc
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";

// entzy models and services
import configEntzy from "components/config/ConfigEntzy";
import { serviceLogError } from "services/graphql/call";

// fonts
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPowerOff as iconActiveSwitch } from "@fortawesome/pro-duotone-svg-icons";
import { faDoNotEnter as iconPrivateSwitch } from "@fortawesome/pro-duotone-svg-icons";
import { faDotCircle as iconFallback } from "@fortawesome/pro-duotone-svg-icons";
import { faStopwatch as iconTimeMin } from "@fortawesome/pro-duotone-svg-icons";
import { faUserGroup as iconUserMin } from "@fortawesome/pro-duotone-svg-icons";
import { faUserCircle as iconUserGroup } from "@fortawesome/pro-duotone-svg-icons";
import { faUsersLine as iconUserMax } from "@fortawesome/pro-duotone-svg-icons";
import { faQrcode as iconTicketText } from "@fortawesome/pro-duotone-svg-icons";
import { faStopwatch as iconAge } from "@fortawesome/pro-regular-svg-icons";

dayjs.extend(utc);
dayjs.extend(customParseFormat);

export const getAdormentIcon = function (icon) {
  let iconValue;
  switch (icon) {
    case "active-switch":
      iconValue = iconActiveSwitch;
      break;
    case "private-switch":
      iconValue = iconPrivateSwitch;
      break;
    case "ticket-text":
      iconValue = iconTicketText;
      break;
    case "time-min":
      iconValue = iconTimeMin;
      break;
    case "user-min":
      iconValue = iconUserMin;
      break;
    case "user-group":
      iconValue = iconUserGroup;
      break;
    case "user-max":
      iconValue = iconUserMax;
      break;
    default:
      iconValue = iconFallback;
      break;
  }
  return <FontAwesomeIcon icon={iconValue} fixedWidth />;
};

export const scrollSnapTop = function () {
  try {
    window.scrollTo({ top: 0, behavior: "smooth" });
  } catch (error) {
    try {
      let target;
      target = document.getElementById("anchor-main-app");
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        target = document.getElementById("anchor-main-content");
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          document.body.scrollTop = 0;
          document.documentElement.scrollTop = 0;
        }
      }
    } catch (error) {
      return false;
    }
  }
  return true;
};

export const scrollTopDone = function () {
  return new Promise((resolve) => {
    scrollSnapTop();
    let iterator = 0;

    const checkIfDone = () => {
      iterator++;
      if (iterator > 50) {
        resolve();
        return;
      }
      if (window.scrollY === 0) {
        resolve();
      } else {
        requestAnimationFrame(checkIfDone);
      }
    };

    requestAnimationFrame(checkIfDone);
  });
};

export const scrollIntoViewIfNeeded = function (target, snap) {
  // if (target.getBoundingClientRect().bottom > window.innerHeight) {
  //   target.scrollIntoView({
  //     behavior: snap ? "instant" : "smooth",
  //     block: "start",
  //   });
  // }
  if (target.getBoundingClientRect().top < 0) {
    target.scrollIntoView({
      behavior: snap ? "instant" : "smooth",
      block: "start",
    });
  }
};

export async function copyToClipboard(text) {
  if ("clipboard" in navigator) {
    return await navigator.clipboard.writeText(text);
  } else {
    return document.execCommand("copy", true, text);
  }
}

export const getDomainFromUrl = function (url) {
  let domain;
  try {
    if (!url.toLowerCase().startsWith("http")) {
      url = "https://" + url;
    }
    domain = new URL(url);
    domain = domain.hostname
      .toLowerCase()
      .replace("www.", "")
      .substring(url, 20);
  } catch (e) {
    domain = url.substring(url, 20);
  }
  return domain;
};

export const parseEntzyUrl = function (val) {
  try {
    let urlparts = ("/" + val).split("/");
    const eventUrl = urlparts[urlparts.length - 1].toLowerCase();
    return eventUrl;
  } catch (error) {
    return false;
  }
};
export const parseEntzyLinkToDataUrl = function (val) {
  try {
    // given val pasted by user as url link need to strip any http or https and host parts and extract data url part
    // e.g. https://<env domain or local entzy.com>/country/eventuatorname -> country.eventuatorname
    let urlparts = ("/" + val).split("/");
    let name = urlparts[urlparts.length - 1].toLowerCase();
    let country = urlparts.length > 2 ? urlparts[urlparts.length - 2] : "e";
    return {
      name: name,
      country: country,
      dataUrl: country + "." + name,
    };
  } catch (error) {
    return {
      name: "",
      country: "e",
      dataUrl: "",
    };
  }
};

export const parseEntzyUrlComponents = function (val) {
  try {
    if (val.indexOf(".") > -1 && val.indexOf("/") === -1) {
      val = val.replace(".", "/");
    }
    if (val.indexOf(".") === -1 && val.indexOf("/") === -1) {
      val = "/e/" + val;
    }

    let urlparts = ("/" + val).split("/");
    let name = urlparts[urlparts.length - 1].toLowerCase();
    let country = urlparts.length > 2 ? urlparts[urlparts.length - 2] : "e";

    if (country.length > 2) {
      country = "e";
    }
    return {
      name: name,
      country: country,
      dataUrl: country === "e" ? name : country + "." + name,
      pointer: "/" + country + "/",
      relativeUrl: "/" + country + "/" + name,
      absoluteUrl: `${configEntzy.APP_URL}/${country}/${name}`,
    };
  } catch (error) {
    return {
      name: "",
      country: "e",
      dataUrl: "",
      pointer: "/e/",
      relativeUrl: "/e/",
      absoluteUrl: `${configEntzy.APP_URL}/e/`,
    };
  }
};

export const isInteger = function (val) {
  try {
    let int = parseInt(val);
    return Number.isInteger(int);
  } catch (error) {
    return false;
  }
};

export const isEmptyString = function (val) {
  return val.trim().length === 0;
};

export const isEmail = function (email) {
  // var re = /\S+@\S+\.\S+/;
  var re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

export const isUrl = function (url) {
  if (url.indexOf(" ") !== -1 || url.indexOf(".") === -1) {
    return false;
  } else {
    return true;
  }
};

export const jsonTryParse = function (str, fallback) {
  try {
    return JSON.parse(str);
  } catch (error) {
    const logCapture = { str, error };
    serviceLogError("jsonTryParse", logCapture);
    return fallback ? fallback : [];
  }
};

// external link flags
export const getExternalTag = () => {
  return "external:";
};
export const isExternalLink = (location) => {
  return location.LinkTarget
    ? location.LinkTarget.startsWith(getExternalTag())
    : false;
};
export const getExternalLink = (location) => {
  return location.LinkTarget
    ? location.LinkTarget.replace(getExternalTag(), "")
    : "";
};

// id generators
// javascript guid generator
// for use client side only until replaced server side
export const idGenerator = (len) => {
  const S4 = function () {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  };
  const ID =
    S4() +
    S4() +
    "-" +
    S4() +
    "-" +
    S4() +
    "-" +
    S4() +
    "-" +
    S4() +
    S4() +
    S4();
  return len ? ID.substring(0, len) : ID;
};
export const idTimeGenerator = (params = {}) => {
  const ts = params.micro ? Date.now() : Math.floor(Date.now() / 1000);
  const rd = Math.floor(Math.random() * 100);
  const id = params.addRandom ? `${ts}${rd}` : `${ts}`;
  return id;
};
export function getCombinedUserId(userId1, userId2) {
  const delimiter = configEntzy.DELIMITER_USERS;
  const sortedStrings = [userId1, userId2].sort();
  const combinedString = sortedStrings[0] + delimiter + sortedStrings[1];
  return combinedString;
}
export const getDefaultLargeInt = function () {
  return 100000000;
};

// dynamodb ttl generator
export function getDynamoDBTTL(days) {
  const currentTime = new Date().getTime();
  const oneDay = 24 * 60 * 60 * 1000; // one day in milliseconds
  const expirationTime = currentTime + days * oneDay;
  return Math.floor(expirationTime / 1000); // TTL requires the timestamp to be in seconds
}

// currencies
export const getCurrencyDisplayAmount = (intValue) => {
  return (intValue / 100).toFixed(2);
};
export const getCurrencyIntAmount = (decValue) => {
  return Math.round(decValue * 100);
};

// converters
export const dataUrlToFile = (dataUrl, filename = "image.jpg") => {
  const [header, base64] = dataUrl.split(",");
  if (!base64) throw new Error("Invalid data URL");

  const mimeMatch = header.match(/data:(.*?);base64/);
  const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";

  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);

  return new File([bytes], filename, { type: mime });
};

// age generator
export const getAge = (props) => {
  let ageValue = "";
  try {
    const currentTimestamp = dayjs.utc();
    const utcTimestamp = props.timestamp;
    const format = "YYYY-MM-DDTHH:mm:sssZ";
    const timestampObj = props.timestampObj
      ? props.timestampObj
      : dayjs.utc(utcTimestamp, format);
    const diffInMins = currentTimestamp.diff(timestampObj, "minute");
    const diffInHours = currentTimestamp.diff(timestampObj, "hour");
    const diffInDays = currentTimestamp.diff(timestampObj, "day");
    // const diffInWeeks = currentTimestamp.diff(timestampObj, "week");
    // const diffInMonths = currentTimestamp.diff(timestampObj, "month");
    const diffInYears = currentTimestamp.diff(timestampObj, "year");

    if (diffInMins < 60) {
      if (diffInMins <= 0) {
        ageValue = "now";
      } else {
        ageValue = `${diffInMins}m`;
      }
    } else if (diffInHours < 24) {
      ageValue = `${diffInHours}h`;
    } else if (diffInDays > 365) {
      ageValue = `${diffInYears}y`;
      // } else if (diffInWeeks < 4) {
      //   ageValue = `${diffInWeeks}wk`;
      // } else if (diffInMonths < 12) {
      //   ageValue = `${diffInMonths}mo`;
    } else {
      ageValue = `${diffInDays}d`;
    }
  } catch (error) {
    ageValue = "earlier";
  }
  return (
    <span>
      <FontAwesomeIcon icon={iconAge} />
      &nbsp;&nbsp;
      {ageValue}
    </span>
  );
};

export const timeout = async (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const closeAction = (
  mainContext,
  eventContext,
  closeTarget,
  menuSelectById,
) => {
  let menuId = closeTarget ? closeTarget : "home";
  menuSelectById(menuId);
  if (mainContext) {
    mainContext.updateViewerTrail({
      memberTab: 0,
      launchpadTab: 0,
    });
    mainContext.selectMenu(menuId);
  }
};

// third party tools
export const googleConvertColorId = (colorId) => {
  let id, value;
  id = colorId ? colorId.toString() : "0";
  switch (id) {
    case "1":
      value = "#A4BDFC";
      break;
    case "2":
      value = "#7AE7BF";
      break;
    case "3":
      value = "#DBADFF";
      break;
    case "4":
      value = "#FF887C";
      break;
    case "5":
      value = "#FBD75B";
      break;
    case "6":
      value = "#FFB878";
      break;
    case "7":
      value = "#46D6DB";
      break;
    case "8":
      value = "#E1E1E1";
      break;
    case "9":
      value = "#5484ED";
      break;
    case "10":
      value = "#51B749";
      break;
    case "11":
      value = "#DC2127";
      break;
    default:
      value = "#000000";
      break;
  }
  return value;
};

// toolbox
const toolbox = function () {
  const tools = {};
  tools.getAdormentIcon = getAdormentIcon;
  tools.scrollSnapTop = scrollSnapTop;
  tools.scrollTopDone = scrollTopDone;
  tools.scrollIntoViewIfNeeded = scrollIntoViewIfNeeded;
  tools.copyToClipboard = copyToClipboard;
  tools.getDomainFromUrl = getDomainFromUrl;
  tools.isInteger = isInteger;
  tools.isEmptyString = isEmptyString;
  tools.isEmail = isEmail;
  tools.isUrl = isUrl;
  tools.parseEntzyUrl = parseEntzyUrl;
  tools.parseEntzyLinkToDataUrl = parseEntzyLinkToDataUrl;
  tools.parseEntzyUrlComponents = parseEntzyUrlComponents;
  tools.jsonTryParse = jsonTryParse;
  tools.getExternalTag = getExternalTag;
  tools.isExternalLink = isExternalLink;
  tools.getExternalLink = getExternalLink;
  tools.idGenerator = idGenerator;
  tools.getDefaultLargeInt = getDefaultLargeInt;
  tools.getCombinedUserId = getCombinedUserId;
  tools.getDynamoDBTTL = getDynamoDBTTL;
  tools.getCurrencyDisplayAmount = getCurrencyDisplayAmount;
  tools.getCurrencyIntAmount = getCurrencyIntAmount;
  tools.dataUrlToFile = dataUrlToFile;
  tools.getAge = getAge;
  tools.timeout = timeout;
  tools.closeAction = closeAction;
  tools.googleConvertColorId = googleConvertColorId;
  return tools;
};

export default toolbox;
